import React, { useState } from 'react';
import { ExternalLink, BookOpen, Clock, Filter } from 'lucide-react';
import { publications } from '../data/mock';
import { useInView } from '../hooks/useInView';

const PublicationsPage = () => {
  const [headerRef, headerInView] = useInView({ threshold: 0.1 });
  const [pubRef, pubInView] = useInView({ threshold: 0.1 });
  const [activeTab, setActiveTab] = useState('published');
  const [activeCategory, setActiveCategory] = useState('All');

  const categories = ['All', ...new Set(publications.peerReviewed.map(p => p.category))];

  const filteredPublications = activeCategory === 'All'
    ? publications.peerReviewed
    : publications.peerReviewed.filter(p => p.category === activeCategory);

  const categoryColors = {
    'All': 'from-[#1F3C88] to-[#2A4FA8]',
    'Governance': 'from-emerald-400 to-green-600',
    'Elections': 'from-amber-400 to-orange-500',
    'Education': 'from-blue-400 to-indigo-600',
    'Social Studies': 'from-purple-400 to-violet-600',
    'Development': 'from-rose-400 to-pink-600',
  };

  const pubColors = [
    { gradient: 'from-emerald-400 to-green-600', shadow: 'shadow-emerald-500/30', bg: 'bg-emerald-50' },
    { gradient: 'from-amber-400 to-orange-500', shadow: 'shadow-amber-500/30', bg: 'bg-amber-50' },
    { gradient: 'from-blue-400 to-indigo-600', shadow: 'shadow-blue-500/30', bg: 'bg-blue-50' },
    { gradient: 'from-purple-400 to-violet-600', shadow: 'shadow-purple-500/30', bg: 'bg-purple-50' },
    { gradient: 'from-rose-400 to-pink-600', shadow: 'shadow-rose-500/30', bg: 'bg-rose-50' },
    { gradient: 'from-cyan-400 to-teal-600', shadow: 'shadow-cyan-500/30', bg: 'bg-cyan-50' },
  ];

  return (
    <>
      {/* Page Header */}
      <div className="relative bg-gradient-to-br from-[#1F3C88] via-[#2A4FA8] to-[#1F3C88] py-32 overflow-hidden" ref={headerRef}>
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{ backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)', backgroundSize: '40px 40px' }} />
        </div>
        <div className="absolute top-0 right-0 w-96 h-96 bg-blue-500/20 rounded-full blur-3xl" />
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-purple-500/20 rounded-full blur-3xl" />
        
        <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
          <div className={`transition-all duration-700 ${headerInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
            <span className="inline-block px-5 py-2 bg-gradient-to-r from-blue-400 to-indigo-600 text-white text-sm font-bold rounded-full mb-6 shadow-lg shadow-blue-500/30">
              ACADEMIC WORK
            </span>
            <h1 className="text-5xl lg:text-7xl font-black text-white mb-6" style={{ fontFamily: "'Montserrat', sans-serif" }}>
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-300 to-indigo-300">Publications</span>
            </h1>
            <p className="text-white/80 max-w-2xl text-xl leading-relaxed font-medium">
              Peer-reviewed journal articles and research papers contributing to sociological scholarship.
            </p>
          </div>
        </div>
      </div>

      {/* Stats */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-3 gap-6">
            {[
              { number: publications.peerReviewed.length, label: "Published Papers", gradient: 'from-emerald-400 to-green-600', shadow: 'shadow-emerald-500/30' },
              { number: publications.upcoming.length, label: "Upcoming", gradient: 'from-amber-400 to-orange-500', shadow: 'shadow-amber-500/30' },
              { number: categories.length - 1, label: "Research Areas", gradient: 'from-purple-400 to-violet-600', shadow: 'shadow-purple-500/30' },
            ].map((stat, index) => (
              <div key={index} className={`rounded-3xl p-8 bg-gradient-to-br ${stat.gradient} shadow-xl ${stat.shadow} transition-all duration-500 hover:scale-105 hover:-translate-y-2`}>
                <p className="text-5xl font-black text-white mb-2" style={{ fontFamily: "'Montserrat', sans-serif" }}>{stat.number}</p>
                <p className="text-white/90 font-semibold">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Publications */}
      <section className="py-20 bg-gradient-to-b from-gray-50 to-white" ref={pubRef}>
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          {/* Tabs */}
          <div className={`flex flex-wrap gap-4 mb-8 transition-all duration-700 ${pubInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
            <button
              onClick={() => setActiveTab('published')}
              className={`px-6 py-3 font-bold text-sm rounded-full transition-all flex items-center gap-2 ${activeTab === 'published' ? 'bg-gradient-to-r from-emerald-400 to-green-600 text-white shadow-lg shadow-emerald-500/30' : 'bg-white text-[#4A5568] hover:bg-gray-50 border-2 border-gray-200'}`}
            >
              <BookOpen className="w-4 h-4" />
              Published ({publications.peerReviewed.length})
            </button>
            <button
              onClick={() => setActiveTab('upcoming')}
              className={`px-6 py-3 font-bold text-sm rounded-full transition-all flex items-center gap-2 ${activeTab === 'upcoming' ? 'bg-gradient-to-r from-amber-400 to-orange-500 text-white shadow-lg shadow-amber-500/30' : 'bg-white text-[#4A5568] hover:bg-gray-50 border-2 border-gray-200'}`}
            >
              <Clock className="w-4 h-4" />
              Upcoming ({publications.upcoming.length})
            </button>
          </div>

          {/* Category Filter */}
          {activeTab === 'published' && (
            <div className={`mb-8 transition-all duration-700 delay-100 ${pubInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
              <div className="flex items-center gap-2 mb-4">
                <Filter className="w-5 h-5 text-[#1F3C88]" />
                <span className="text-[#1C1C1C] font-bold">Filter by category:</span>
              </div>
              <div className="flex flex-wrap gap-2">
                {categories.map((cat) => (
                  <button
                    key={cat}
                    onClick={() => setActiveCategory(cat)}
                    className={`px-5 py-2 text-sm font-bold rounded-full transition-all ${activeCategory === cat ? `bg-gradient-to-r ${categoryColors[cat] || 'from-[#1F3C88] to-[#2A4FA8]'} text-white shadow-lg` : 'bg-white text-[#4A5568] hover:bg-gray-50 border-2 border-gray-200'}`}
                  >
                    {cat}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Published Papers */}
          {activeTab === 'published' && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {filteredPublications.map((pub, index) => {
                const colorSet = pubColors[index % pubColors.length];
                return (
                  <div
                    key={pub.id}
                    className={`group bg-white rounded-3xl p-6 border-2 border-gray-100 hover:border-transparent hover:shadow-2xl transition-all duration-500 hover:-translate-y-2 ${pubInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
                    style={{ transitionDelay: `${(index % 6) * 100}ms` }}
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex flex-wrap items-center gap-2 mb-4">
                          <span className={`bg-gradient-to-r ${colorSet.gradient} text-white px-4 py-1.5 rounded-full text-xs font-bold shadow-lg ${colorSet.shadow}`}>
                            {pub.category}
                          </span>
                          <span className="bg-[#1F3C88] text-white px-3 py-1 rounded-full text-xs font-bold">
                            {pub.year}
                          </span>
                        </div>
                        <h3 className="text-[#1C1C1C] font-black text-lg mb-3 group-hover:text-[#1F3C88] transition-colors" style={{ fontFamily: "'Montserrat', sans-serif" }}>
                          {pub.title}
                        </h3>
                        <p className="text-[#718096] font-medium">
                          {pub.journal}
                        </p>
                      </div>
                      <a
                        href={pub.doi}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={`flex-shrink-0 w-14 h-14 rounded-2xl bg-gradient-to-br ${colorSet.gradient} flex items-center justify-center text-white shadow-lg ${colorSet.shadow} hover:scale-110 transition-transform`}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <ExternalLink className="w-6 h-6" />
                      </a>
                    </div>
                    <a
                      href={pub.doi}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 text-[#1F3C88] font-bold text-sm mt-4 hover:gap-3 transition-all"
                    >
                      Read Full Paper
                      <ExternalLink className="w-4 h-4" />
                    </a>
                  </div>
                );
              })}
            </div>
          )}

          {/* Upcoming Papers */}
          {activeTab === 'upcoming' && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {publications.upcoming.map((pub, index) => {
                const colorSet = pubColors[index % pubColors.length];
                return (
                  <div
                    key={pub.id}
                    className={`rounded-3xl p-8 bg-gradient-to-br ${colorSet.gradient} shadow-xl ${colorSet.shadow} transition-all duration-500 hover:scale-105 hover:-translate-y-2 ${pubInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
                    style={{ transitionDelay: `${index * 100}ms` }}
                  >
                    <div className="flex items-center gap-2 mb-4">
                      <Clock className="w-5 h-5 text-white" />
                      <span className="bg-white/20 text-white px-3 py-1 rounded-full text-xs font-bold">
                        {pub.status}
                      </span>
                    </div>
                    <h3 className="text-white font-black text-lg leading-snug" style={{ fontFamily: "'Montserrat', sans-serif" }}>
                      {pub.title}
                    </h3>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </section>
    </>
  );
};

export default PublicationsPage;